import { Badge } from '@civilgrid/ui/components';
import { SkeletonSpinnerCentered } from '@civilgrid/ui/SkeletonLoader';
import { getCoreRowModel, getSortedRowModel, useReactTable } from '@tanstack/react-table';
import { useEffect, useMemo } from 'react';
import { useAsyncFn } from 'react-use';
import { getHistoricalUSGSMaps } from '~/api/projects2';
import { AUTO_COL_WIDTH, TableV2 } from '~/components2/Table';

interface Props {
  projectId: string;
}

export default function HistoricalUSGSMaps({ projectId }: Props) {
  const [projectLoader, loadProject] = useAsyncFn(async (projectId: string) => {
    return (await getHistoricalUSGSMaps(projectId)) as {
      id: string;
      thumbnail: string;
      title: string;
      publisher: string;
      series: string;
      scale: number;
      year: number;
      bed_surf: number;
      gis: number | null;
      link: string;
    }[];
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  useEffect(() => {
    loadProject(projectId);
  }, []);

  const table = useReactTable({
    data: projectLoader.loading ? [] : projectLoader.value!,
    columns: [
      {
        id: 'year',
        header: 'Year',
        accessorKey: 'year',
        size: 75,
        cell: (cell) => {
          const { year } = cell.row.original;
          return <div className="text-sm">{year}</div>;
        },
        //@ts-expect-error
        cellClassName: 'align-top',
      },
      {
        id: 'title',
        header: 'Title',
        accessorKey: 'title',
        size: AUTO_COL_WIDTH,
        cell: (cell) => {
          const { title, series, publisher, scale, gis, bed_surf } = cell.row.original;
          const containsGIS = gis !== null;

          const badges = [];
          containsGIS &&
            badges.push(
              <Badge key="GIS" color="grey">
                GIS
              </Badge>,
            );

          if (bed_surf === 5) {
            badges.push(
              <Badge key="bed" color="purple">
                Bedrock
              </Badge>,
            );
          } else if (bed_surf === 6) {
            badges.push(
              <Badge key="surf" color="red">
                Surficial
              </Badge>,
            );
          }

          return (
            <div className="flex flex-col space-y-1">
              <div className="text-sm">{title}</div>
              <div className="text-xs">
                <span className="text-orange-900">{series}</span>, <span className="text-blue-600">{publisher}</span>,{' '}
                <span className="text-green-900">1:{scale}</span>
              </div>
              <div className="flex space-x-1">{badges}</div>
            </div>
          );
        },
      },
    ],
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    enableSorting: true,
    initialState: {
      sorting: [{ id: 'year', desc: true }],
    },
  });

  const theTable = (
    <TableV2
      id="historical-usgs-v1"
      table={table}
      onRowClick={(row) => {
        window.open(row.original.link, '_blank');
      }}
    />
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  const content = useMemo(() => {
    if (projectLoader.loading) {
      return <SkeletonSpinnerCentered />;
    } else {
      if (projectLoader.error) {
        return <div>⚠️ We were unable to load the data. Our team is investigating the issue.</div>;
      }
      if (!projectLoader.value) {
        return null;
      }

      return theTable;
    }
  }, [projectLoader.loading, theTable]);

  return (
    <div className="group w-full">
      <div className=" flex flex-row items-center space-x-2">
        <div className="w-4/5 py-2 text-base font-semibold">Historical USGS Maps & Studies</div>
      </div>
      <div className="w-full">{content}</div>
    </div>
  );
}
